import { useState, useCallback, useEffect, useRef } from "react";
import { getStartAndEndDate } from "../../utils";
import { analytics, triggerLogEvent } from "../../config/firebase";
import { useReportDateContext } from "../../context/ReportDateContext";

const fmtDate = (d) => {
  try {
    const date = d?.toDate ? d.toDate() : new Date(d);
    return date.toISOString().split("T")[0];
  } catch {
    return null;
  }
};

const useReportFilter = (onFetch, reportType) => {
  const sharedDateRef = useReportDateContext();

  const initialShared = useRef(() => {
    const shared = sharedDateRef?.current;
    return shared?.startDate && shared?.endDate ? shared : null;
  }).current();

  const initialFilter = initialShared?.filter || "Yearly";

  const initialDates = useRef(
    initialShared
      ? { startDate: initialShared.startDate, endDate: initialShared.endDate }
      : getStartAndEndDate("Yearly")
  ).current;

  const [headerDefaultFilter, setHeaderDefaultFilter] = useState(initialFilter);
  const [isYearlyFilter, setIsYearlyFilter] = useState(initialFilter === "Yearly");
  const [resetCount, setResetCount] = useState(0);

  // Initial fetch — uses shared date if coming from another report, else default "30 days"
  useEffect(() => {
    onFetch(initialDates.startDate, initialDates.endDate);
  }, [onFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFilterDate = useCallback((startDate, endDate, filter) => {
    if (!startDate || !endDate) return;
    setIsYearlyFilter(filter === "Yearly");
    if (sharedDateRef) sharedDateRef.current = { startDate, endDate, filter: filter || null };
    onFetch(startDate, endDate);
    triggerLogEvent(analytics, `report_${reportType}_filter_applied`, {
      filter: filter || "custom",
      start_date: fmtDate(startDate),
      end_date: fmtDate(endDate),
    });
  }, [onFetch, reportType, sharedDateRef]);

  const handleResetFilter = useCallback(() => {
    const { startDate, endDate } = getStartAndEndDate("Yearly");
    if (sharedDateRef) sharedDateRef.current = { startDate, endDate, filter: "Yearly" };
    onFetch(startDate, endDate);
    setIsYearlyFilter(true);
    setHeaderDefaultFilter("Yearly");
    setResetCount(c => c + 1);
    triggerLogEvent(analytics, `report_${reportType}_filter_applied`, {
      filter: "Yearly",
      start_date: fmtDate(startDate),
      end_date: fmtDate(endDate),
    });
  }, [onFetch, reportType, sharedDateRef]);

  return {
    headerDefaultFilter,
    isYearlyFilter,
    resetCount,
    handleFilterDate,
    handleResetFilter,
  };
};

export default useReportFilter;
